import { Flex, Input } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Search } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useIconSizes } from 'src/hooks'

import { ColumnSettingsSections } from './ColumnSettingsSections/ColumnSettingsSections'
import { useStyles } from './styles'
import type {
  ColumnSettingsConfig,
  ColumnSettingsHiddenGroup,
  ColumnStateMap
} from './types'
import {
  hideAllColumns,
  hideColumn,
  moveColumnsBetweenGroups,
  pinColumnAndShow,
  showColumn,
  showColumns,
  splitColumnSettings,
  toColumnSettingsItems
} from './utils'

const NO_LOCKED_KEYS: string[] = []
const NO_HIDDEN_GROUPS: ColumnSettingsHiddenGroup[] = []

type Props<T> = {
  columns: ColumnsType<T>
  columnsMap: ColumnStateMap | undefined
  icons?: ColumnSettingsConfig['icons']
  hints?: ColumnSettingsConfig['hints']
  lockedKeys?: string[]
  hiddenGroups?: ColumnSettingsHiddenGroup[]
  pinnable?: boolean
  keepOneOnHideAll?: boolean
  searchable?: boolean
  onChange: (columnsMap: ColumnStateMap) => void
}

export const ColumnSettingsPanel = <T,>({
  columns,
  columnsMap,
  icons,
  hints,
  lockedKeys = NO_LOCKED_KEYS,
  hiddenGroups = NO_HIDDEN_GROUPS,
  pinnable = true,
  keepOneOnHideAll = true,
  searchable = true,
  onChange
}: Props<T>) => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const iconSizes = useIconSizes()
  const [query, setQuery] = useState('')

  const items = toColumnSettingsItems(columns, lockedKeys)
  const split = splitColumnSettings(items, columnsMap)

  const onToggle = (key: string, isShown: boolean) =>
    onChange(
      isShown
        ? showColumn(split, key, columnsMap)
        : hideColumn(split, key, columnsMap)
    )

  return (
    <Flex vertical gap="small">
      {searchable && (
        <Input
          className={styles.search}
          autoFocus
          allowClear
          value={query}
          prefix={<Search size={iconSizes.small} />}
          placeholder={t('column_settings_search')}
          aria-label={t('column_settings_search')}
          onChange={(event) => setQuery(event.target.value)}
        />
      )}

      <ColumnSettingsSections
        split={split}
        columnsMap={columnsMap}
        query={query}
        icons={icons}
        hints={hints}
        hiddenGroups={hiddenGroups}
        pinnable={pinnable}
        keepOneOnHideAll={keepOneOnHideAll}
        onToggle={onToggle}
        onPin={(key, side) =>
          onChange(pinColumnAndShow(split, key, side, columnsMap))
        }
        onHideAll={() =>
          onChange(hideAllColumns(split, columnsMap, keepOneOnHideAll))
        }
        onShowColumns={(keys) => onChange(showColumns(split, keys, columnsMap))}
        onMove={(shownKeys, hiddenKeys) => {
          const moved = moveColumnsBetweenGroups(
            items,
            shownKeys,
            hiddenKeys,
            columnsMap
          )

          if (moved) {
            onChange(moved)
          }
        }}
      />
    </Flex>
  )
}
